import { jsonBody, unwrap } from "./foyer";
import type {
  ShowcaseAnalytics,
  ShowcaseVideo,
  ShowcaseVideoPatch,
} from "@shared/showcase";
import type {
  ShowcaseCaptionLang,
  ShowcaseCaptionState,
  ShowcaseCue,
} from "@shared/showcaseCaptions";

/** CMS calls for video.nenda.com (docs/design/video-nenda.md). */
const BASE = "/api/showcase/v1";

export interface ShowcaseConfig {
  configured: boolean;
  blocker: string | null;
  publicBaseUrl: string;
}

export interface UploadPlan {
  rev: number;
  partBytes: number;
  parts: { partNumber: number; url: string }[];
}

export const showcaseApi = {
  config(): Promise<ShowcaseConfig> {
    return unwrap(fetch(`${BASE}/config`));
  },
  list(): Promise<{ videos: ShowcaseVideo[] }> {
    return unwrap(fetch(`${BASE}/videos`));
  },
  get(id: string): Promise<ShowcaseVideo> {
    return unwrap(fetch(`${BASE}/videos/${encodeURIComponent(id)}`));
  },
  create(title: string): Promise<ShowcaseVideo> {
    return unwrap(fetch(`${BASE}/videos`, jsonBody("POST", { title })));
  },
  update(id: string, patch: ShowcaseVideoPatch): Promise<ShowcaseVideo> {
    return unwrap(
      fetch(
        `${BASE}/videos/${encodeURIComponent(id)}`,
        jsonBody("PATCH", patch)
      )
    );
  },
  async remove(id: string): Promise<void> {
    const res = await fetch(`${BASE}/videos/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
    if (!res.ok && res.status !== 404) throw new Error(`http_${res.status}`);
  },
  beginUpload(
    id: string,
    file: { name: string; type: string; size: number }
  ): Promise<UploadPlan> {
    return unwrap(
      fetch(
        `${BASE}/videos/${encodeURIComponent(id)}/upload`,
        jsonBody("POST", {
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
          bytes: file.size,
        })
      )
    );
  },
  completeUpload(
    id: string,
    body: {
      parts: { partNumber: number; etag: string }[];
      durationSeconds: number | null;
      width: number | null;
      height: number | null;
      poster: string | null;
    }
  ): Promise<ShowcaseVideo> {
    return unwrap(
      fetch(
        `${BASE}/videos/${encodeURIComponent(id)}/upload/complete`,
        jsonBody("POST", body)
      )
    );
  },
  abortUpload(id: string): Promise<ShowcaseVideo> {
    return unwrap(
      fetch(`${BASE}/videos/${encodeURIComponent(id)}/upload/abort`, {
        method: "POST",
      })
    );
  },
  setPoster(id: string, poster: string): Promise<ShowcaseVideo> {
    return unwrap(
      fetch(
        `${BASE}/videos/${encodeURIComponent(id)}/poster`,
        jsonBody("POST", { poster })
      )
    );
  },
  captions(id: string): Promise<ShowcaseCaptionState> {
    return unwrap(fetch(`${BASE}/videos/${encodeURIComponent(id)}/captions`));
  },
  generateCaptions(id: string): Promise<ShowcaseCaptionState> {
    return unwrap(
      fetch(`${BASE}/videos/${encodeURIComponent(id)}/captions/generate`, {
        method: "POST",
      })
    );
  },
  saveCaptions(
    id: string,
    lang: ShowcaseCaptionLang,
    body: { cues?: ShowcaseCue[]; approved: boolean }
  ): Promise<ShowcaseCaptionState> {
    return unwrap(
      fetch(
        `${BASE}/videos/${encodeURIComponent(id)}/captions/${lang}`,
        jsonBody("PUT", body)
      )
    );
  },
  analytics(id: string): Promise<ShowcaseAnalytics> {
    return unwrap(fetch(`${BASE}/videos/${encodeURIComponent(id)}/analytics`));
  },
};
